import { merge } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';

import { isEmptyString } from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { GoogleSupportedLanguage } from 'dashboard/lib/constants/locales';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setTranslations, Translations } from 'dashboard/redux/slices/translations';
import { RootState } from 'dashboard/redux/store';
import { type InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import { isTooManyRequestsError } from 'lib/permissions-errors';

export type TranslationRequests = Array<
  [string, (GoogleSupportedLanguage | InquiryIso6391LanguageCode)[]]
>;
type UseTranslationsResult = {
  translations: Translations;
  loading: boolean;
  requestTranslations: (translationRequests: TranslationRequests) => Promise<Translations>;
};

export default function useTranslations(): UseTranslationsResult {
  const dispatch = useDispatch();
  const translations = useSelector<RootState, Translations>((state) => state.translations);

  const {
    error,
    loading,
    callback: apiCallback,
  } = useApiCallback(async (translationRequests: TranslationRequests, token?: string) => {
    const response = await SessionClientDashboard.postResourceSingletonAction<
      ApiResource<null>,
      { type: ApiModel.Translations },
      { translations: Translations }
    >({ type: ApiModel.Translations }, 'translate', {
      meta: { 'translation-requests': translationRequests, token: token || null },
    });

    const translations = response.meta?.translations;

    if (translations) {
      dispatch(setTranslations(translations));
    }
    return translations;
  }, []);

  const handleError = (text: string) =>
    dispatch(showMessage(`${text} Please try again.`, MessageType.Failure));

  useApiErrorHandler({
    error,
    onError: (error) => {
      if (isTooManyRequestsError(error)) {
        handleError('Too many translations requested.');
      } else {
        handleError('There was a problem translating your text.');
      }
    },
    onNetworkError: () => handleError('There was a network issue.'),
  });

  const notTranslated = (
    sourceText: string,
    locale: GoogleSupportedLanguage | InquiryIso6391LanguageCode,
  ): boolean => translations?.[sourceText]?.[locale] == null;

  const requestTranslations = async (
    translationRequests: TranslationRequests,
  ): Promise<Translations> => {
    const newTranslationRequests = translationRequests
      .map(([sourceText, locales]) => {
        if (isEmptyString(sourceText)) return null;
        // filter out text+locale pairs that have cached translations in redux
        const nonTranslatedLocales = locales.filter((locale) => notTranslated(sourceText, locale));
        return nonTranslatedLocales.length > 0 ? [sourceText, nonTranslatedLocales] : null;
      })
      .filter(Boolean) as TranslationRequests;
    if (newTranslationRequests.length > 0) {
      const newTranslations = await apiCallback(newTranslationRequests);
      // specifically need to use merge here to combine nested translations
      return merge(translations, newTranslations);
    } else {
      return translations;
    }
  };

  return { translations, loading, requestTranslations };
}
